import React, { useEffect } from 'react';

import CHorizontalScrollContainer from '../../../components/CHorizontalScrollContainer';
import CLocalizedText from '../../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';
import CPageModule from '../../../components/CPageModule';
import CPageModulePaginatedTable from '../../../components/CPageModulePaginatedTable';
import CPandasTable from '../../../components/CPandasTable';
import CStatefulContainer from '../../../components/CStatefulContainer';
import TableHyperlinkButton from '../../../components/TableHyperlinkButton';
import Page from '../../../components/layout/Page';
import { EAppType } from '../../../enums/AppType';
import { PageRoutes } from '../../../enums/enums';
import HGeneric from '../../../helpers/HGeneric';
import formatTableColumn from '../../../helpers/formatTableColumn';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../../helpers/helper';
import { useAppSelector } from '../../../hooks/hooks';
import { useUsageTrackingInfo } from '../../../hooks/useUsageTrackingInfo';
import type { CachedAPIResponse } from '../../../models/CachedAPIResponse';
import type { IAugmentedBackendResponse } from '../../../models/IAugmentedBackendResponse';
import type { IRequestParams } from '../../../models/IRequestParams';
import type { IResponse } from '../../../models/IResponse';
import type { IOpsNewsLatest } from '../../../models/OpsNewsResponse';
import type { TPandasTable } from '../../../models/TPandasTable';
import type { IUsageTrackingInfo } from '../../../models/usage-tracking/IUsageTracking';
import SvOpsNewsServices from '../../../services/SvOpsNewsServices';
import { SvUsageTrackingServices } from '../../../services/SvUsageTrackingServices';
import type { PortfolioManagementOutletContext } from '../PPortfolioManagementOutlet';
import { useOutletContext } from 'react-router-dom';

interface State {
  latest: IResponse<CachedAPIResponse<IOpsNewsLatest>>;
  mergers: IResponse<CachedAPIResponse<IAugmentedBackendResponse<TPandasTable>>> & IRequestParams;
  dividends: IResponse<CachedAPIResponse<IAugmentedBackendResponse<TPandasTable>>> & IRequestParams;
}

export default function PPorfolioManagementEvents() {
  const portfolioManagementTabHeading = CLocalizedTextStringStatic('portfolioManagement.nav.events');
  const placeholderCompanyName = CLocalizedTextStringStatic('placeholderCompanyName');
  const usageTrackingParams: IUsageTrackingInfo = useUsageTrackingInfo(EAppType.PortfolioManagementEvent);
  const cacheLabel = CLocalizedTextStringStatic('globalCacheUpdatedAt');

  const { locale } = useAppSelector((state) => state.dashboard);
  const { factsheetId } = useOutletContext<PortfolioManagementOutletContext>();
  const factsheetID = factsheetId ?? '';

  const [state, setState, getState] = useStateExtended<State>({
    latest: { state: { isLoading: true, isError: false } },
    mergers: {
      state: { isLoading: true, isError: false },
      params: { sorting: { sortCol: 3, ascending: 'descending' } }
    },
    dividends: {
      state: { isLoading: true, isError: false },
      params: { sorting: { sortCol: 4, ascending: 'ascending' } }
    }
  });

  useEffect(() => {
    (async () => {
      const currentState = await getState();

      const mergersParams = {
        page: currentState.mergers.data?.cacheData?.meta?.pagination?.page ?? 1,
        sorting: {
          sortCol: currentState.mergers.params?.sorting?.sortCol ?? 4,
          ascending: currentState.mergers.params?.sorting?.ascending ?? 'ascending'
        },
        search: currentState.mergers.params?.search ?? ''
      };

      const dividendsParams = {
        page: currentState.dividends.data?.cacheData?.meta?.pagination?.page ?? 1,
        sorting: {
          sortCol: currentState.dividends.params?.sorting?.sortCol ?? 3,
          ascending: currentState.dividends.params?.sorting?.ascending ?? 'ascending'
        },
        search: currentState.dividends.params?.search ?? ''
      };

      await Promise.all([
        updateStateResponseStatefulAttribute<State, typeof state.latest.data>(
          { latest: currentState.latest },
          setState,
          async () => await SvOpsNewsServices.getLatest({ factsheetID })
        ),
        updateStateResponseStatefulAttribute<State, typeof state.mergers.data>(
          { mergers: currentState.mergers },
          setState,
          async () =>
            await SvOpsNewsServices.getMergers({
              factsheetID,
              ...mergersParams
            })
        ),
        updateStateResponseStatefulAttribute<State, typeof state.dividends.data>(
          { dividends: currentState.dividends },
          setState,
          async () =>
            await SvOpsNewsServices.getDividends({
              factsheetID,
              ...dividendsParams
            })
        )
      ]);
    })();
  }, [locale, factsheetID]);

  const augmentedSplitsTable = state.latest.data?.cacheData.stock_splits
    ? formatTableColumn(state.latest.data.cacheData.stock_splits, 0, (col, row) =>
      row[row.length - 1] ? (
        <TableHyperlinkButton to={`/${PageRoutes.SINGLE_STOCK_OUTLET}/${row[row.length - 1]}`}>
          {col}
        </TableHyperlinkButton>
      ) : (
        col
      )
    )
    : undefined;

  const augmentedMergerAcquisitionsTable = state.mergers.data?.cacheData
    ? {
      ...state.mergers.data.cacheData,
      results: formatTableColumn(state.mergers.data.cacheData.results, 0, (col, row) =>
        row[row.length - 1] ? (
          <TableHyperlinkButton to={`/${PageRoutes.SINGLE_STOCK_OUTLET}/${row[row.length - 1]}`}>
            {col}
          </TableHyperlinkButton>
        ) : (
          col
        )
      )
    }
    : undefined;

  const augmentedDividendsTable = state.dividends.data?.cacheData
    ? {
      ...state.dividends.data.cacheData,
      results: formatTableColumn(state.dividends.data.cacheData.results, 0, (col, row) =>
        row[row.length - 1] ? (
          <TableHyperlinkButton to={`/${PageRoutes.SINGLE_STOCK_OUTLET}/${row[row.length - 1]}`}>
            {col}
          </TableHyperlinkButton>
        ) : (
          col
        )
      )
    }
    : undefined;

  const augmentedIsinChangeTable = state.latest.data?.cacheData.isin_changes
    ? formatTableColumn(state.latest.data.cacheData.isin_changes, 4, (col, row) =>
      row[row.length - 1] ? (
        <TableHyperlinkButton to={`/${PageRoutes.SINGLE_STOCK_OUTLET}/${row[row.length - 1]}`}>
          {col}
        </TableHyperlinkButton>
      ) : (
        col
      )
    )
    : undefined;

  const augmentedTradingItemIDChangeTable = state.latest.data?.cacheData.tradingitemid_changes
    ? formatTableColumn(state.latest.data.cacheData.tradingitemid_changes, 4, (col, row) =>
      row[row.length - 1] ? (
        <TableHyperlinkButton to={`/${PageRoutes.SINGLE_STOCK_OUTLET}/${row[row.length - 1]}`}>
          {col}
        </TableHyperlinkButton>
      ) : (
        col
      )
    )
    : undefined;

  useEffect(() => {
    (async () => {
      await SvUsageTrackingServices.createUsageTrackingRecord(usageTrackingParams);
    })();
  }, []);

  return (
    <Page title={portfolioManagementTabHeading}>
      <CPageModule fullWidth>
        <CPageModule submodule>
          <h3>
            <CLocalizedText dictKey={'opsP2H1'} />
          </h3>
          <p>
            <CLocalizedText dictKey={'opsP2Desc'} />
          </p>
        </CPageModule>
        <CStatefulContainer apiRequestState={state.latest.state} showContent={!!state.latest.data} submodule>
          <CHorizontalScrollContainer>
            <CPandasTable
              data={state.latest.data?.cacheData?.calendar_events}
              colAlignmentArr={['right', 'right', 'right']}
              colSpanArr={[1, 1, 1]}
              colFormatArr={[
                { in: 'tzTimestamp', out: 'localeDateString' },
                { in: 'tzTimestamp', out: 'localeDateString' },
                { in: 'tzTimestamp', out: 'localeDateString' }
              ]}
            />
          </CHorizontalScrollContainer>
          {!!state.latest.data && (
            <CPageModule submodule style={{ paddingTop: 0 }}>
              <p className={'text-xs text-right pr-4 mt-3'}>
                {cacheLabel}
                {HGeneric.formatString((state.latest.data.cacheTimestamp ?? 0) * 1000, {
                  in: 'tzTimestamp',
                  out: 'localTimestampExtended'
                })}
              </p>
            </CPageModule>
          )}
        </CStatefulContainer>
      </CPageModule>

      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CPageModule submodule>
          <h3>
            <CLocalizedText dictKey={'opsP3H1'} />
          </h3>
          <p>
            <CLocalizedText dictKey={'opsP3Desc'} />
          </p>
        </CPageModule>
        <CStatefulContainer apiRequestState={state.latest.state} showContent={!!state.latest.data} submodule>
          <CHorizontalScrollContainer>
            <CPandasTable
              data={augmentedSplitsTable}
              colAlignmentArr={['left', 'left', 'left', 'right', 'right', 'right', 'right']}
              colFormatArr={[
                undefined,
                undefined,
                undefined,
                { in: 'tzTimestamp', out: 'localeDateString' },
                undefined,
                { in: 'boolean', out: 'booleanString' },
                { in: 'boolean', out: 'booleanString' }
              ]}
              colSpanArr={[3, 1.5, 1, 1, 1.5, 1, 1, 0]}
            />
          </CHorizontalScrollContainer>
          {!!state.latest.data && (
            <CPageModule submodule style={{ paddingTop: 0 }}>
              <p className={'text-xs text-right pr-4 mt-3'}>
                {cacheLabel}
                {HGeneric.formatString((state.latest.data.cacheTimestamp ?? 0) * 1000, {
                  in: 'tzTimestamp',
                  out: 'localTimestampExtended'
                })}
              </p>
            </CPageModule>
          )}
        </CStatefulContainer>
      </CPageModule>

      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CPageModule submodule>
          <h3>
            <CLocalizedText dictKey={'opsP4H1'} />
          </h3>
          <p>
            <CLocalizedText dictKey={'opsP4Desc'} />
          </p>
        </CPageModule>
        <CStatefulContainer apiRequestState={state.mergers.state} showContent={!!state.mergers.data} submodule>
          <CPageModulePaginatedTable
            state={state.mergers.state}
            searchBarPlaceholder={placeholderCompanyName}
            data={augmentedMergerAcquisitionsTable}
            colFormatArr={[
              undefined,
              undefined,
              undefined,
              { in: 'tzTimestamp', out: 'localeDateString' },
              undefined,
              { in: 'linkString', out: 'linkHrefBlank' },
              { in: 'boolean', out: 'booleanString' },
              { in: 'boolean', out: 'booleanString' }
            ]}
            colAlignmentArr={['left', 'left', 'right', 'right', 'left', 'right', 'right']}
            colSpanArr={[2, 1.5, 1.5, 1.5, 4.5, 0, 1, 1, 0]}
            searchBarName={'companies'}
            onChange={async (page, sorting, search) => {
              await updateStateResponseStatefulAttribute<State, typeof state.mergers.data>(
                { mergers: (await getState()).mergers },
                setState,
                async () => await SvOpsNewsServices.getMergers({
                  factsheetID, search:search ?? '', page, sorting
                })
              )
              ;
              updateStateAttribute<State>(
                {
                  mergers: { ...(await getState()).mergers, params: { sorting, search } }
                },
                setState
              );
            }}
            params={state.mergers.params}
            rowLinks={Object.values(state.mergers.data?.cacheData?.results?.link ?? {})}
            cachedData={{
              timeToLive: state.mergers.data?.timeToLive ?? 0,
              cacheTimestamp: (state.mergers.data?.cacheTimestamp ?? 0) * 1000
            }}
          />
        </CStatefulContainer>
      </CPageModule>

      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CPageModule submodule>
          <h3>
            <CLocalizedText dictKey={'opsP5H1'} />
          </h3>
          <p>
            <CLocalizedText dictKey={'opsP5Desc'} />
          </p>
        </CPageModule>
        <CStatefulContainer apiRequestState={state.dividends.state} showContent={!!state.dividends.data} submodule>
          <CPageModulePaginatedTable
            state={state.dividends.state}
            data={augmentedDividendsTable}
            searchBarName={'dividends'}
            searchBarPlaceholder={placeholderCompanyName}
            colSpanArr={[2, 1, 1, 0.8, 1, 0.8, 0.8, 0]}
            colAlignmentArr={['left', 'left', 'left', 'right', 'right', 'right', 'right']}
            colFormatArr={[
              undefined,
              undefined,
              undefined,
              undefined,
              { in: 'tzTimestamp', out: 'localeDateString' },
              { in: 'boolean', out: 'booleanString' },
              { in: 'boolean', out: 'booleanString' }
            ]}
            onChange={async (page, sorting, search) => {
              await updateStateResponseStatefulAttribute<State, typeof state.dividends.data>(
                { dividends: (await getState()).dividends },
                setState,
                async () =>
                  await SvOpsNewsServices.getDividends({
                    factsheetID,
                    search: search ?? '',
                    page,
                    sorting
                  })
              );
              updateStateAttribute<State>(
                {
                  dividends: { ...(await getState()).dividends, params: { sorting, search } }
                },
                setState
              );
            }}
            params={state.dividends.params}
            cachedData={{
              timeToLive: state.dividends.data?.timeToLive ?? 0,
              cacheTimestamp: (state.dividends.data?.cacheTimestamp ?? 0) * 1000
            }}
          />
        </CStatefulContainer>
      </CPageModule>

      <CPageModule fullWidth style={{ paddingTop: 0 }}>
        <CPageModule submodule>
          <h3>
            <CLocalizedText dictKey={'opsP6H1'} />
          </h3>
          <p>
            <CLocalizedText dictKey={'opsP6Desc'} />
          </p>
        </CPageModule>
        <CStatefulContainer apiRequestState={state.latest.state} showContent={!!state.latest.data} submodule>
          <CHorizontalScrollContainer>
            <CPandasTable data={augmentedIsinChangeTable} colSpanArr={[1, 1, 1, 1, 1, 0]} />
          </CHorizontalScrollContainer>
          {!!state.latest.data && (
            <CPageModule submodule style={{ paddingTop: 0 }}>
              <p className={'text-xs text-right pr-4 mt-3'}>
                {cacheLabel}
                {HGeneric.formatString((state.latest.data.cacheTimestamp ?? 0) * 1000, {
                  in: 'tzTimestamp',
                  out: 'localTimestampExtended'
                })}
              </p>
            </CPageModule>
          )}
        </CStatefulContainer>
        <CPageModule submodule>
          <p>
            <CLocalizedText dictKey={'opsP6Desc2'} />
          </p>
        </CPageModule>
        <CStatefulContainer apiRequestState={state.latest.state} showContent={!!state.latest.data} submodule>
          <CHorizontalScrollContainer>
            <CPandasTable data={augmentedTradingItemIDChangeTable} colSpanArr={[1, 1, 1, 1, 1, 0]} />
          </CHorizontalScrollContainer>
          {!!state.latest.data?.cacheData.tradingitemid_changes && (
            <CPageModule submodule style={{ paddingTop: 0 }}>
              <p className={'text-xs text-right pr-4 mt-3'}>
                {cacheLabel}
                {HGeneric.formatString((state.latest.data.cacheTimestamp ?? 0) * 1000, {
                  in: 'tzTimestamp',
                  out: 'localTimestampExtended'
                })}
              </p>
            </CPageModule>
          )}
        </CStatefulContainer>
      </CPageModule>
    </Page>
  );
}
